import {
  getCurrentPhotoFillCoveredSize,
  resolveEffectivePhotoFillNaturalSize,
} from '@/lib/photos/photo-fill-resolution'
import { type Point, type Rect, rotatePointAbout } from '@/lib/shapes/shape-coordinates'
import type { ShapePhotoFill } from '@/lib/photos/types/shape-photo-fill'

function rectCorners(rect: Rect): Point[] {
  return [
    { x: rect.x, y: rect.y },
    { x: rect.x + rect.width, y: rect.y },
    { x: rect.x + rect.width, y: rect.y + rect.height },
    { x: rect.x, y: rect.y + rect.height },
  ]
}

/**
 * Pans a photo fill within its frame (drag-to-reposition in crop mode) — moves `offsetX`/
 * `offsetY` by a local (pre-rotation) delta in pt, clamped so the covered photo can never expose
 * empty space past whatever actually needs to stay covered. Mirrors the offset clamp in
 * `computePhotoFillOffsetAfterResize` (`photo-fill-resize-offset.ts`), which applies the same
 * bound after a frame resize; this is the equivalent for a frame-size-preserving pan. Falls back
 * to an unclamped pan when natural size isn't known yet (nothing to clamp against).
 *
 * The photo only ever needs to cover what's actually VISIBLE — `visualRect` (the shape's tight,
 * possibly off-center visual box, in local coordinates relative to the frame's own origin; see
 * `visualRectLocalOfShape`) — not the full `frameWidth`/`frameHeight` bounding box a
 * non-rectangular shape (triangle, star, custom path) clips down from. Covering only the box
 * would be *sufficient* (the box is a superset of the visual region) but needlessly restrictive:
 * the box's corners outside the visible shape are clipped away and never rendered, so requiring
 * the photo to reach them wastes real pan range for no visible benefit — and, since the visual box
 * can sit off-center within the bounding box, clamping symmetrically around the box's own center
 * can block one direction well before the frame's actual edge while leaving slack unused on the
 * other (WMPCM "can't pan a photo in a triangle all the way to one edge, despite visible room").
 * Omit `visualRect` (or pass the full frame) to fall back to the old whole-box behavior — exact
 * for a plain rectangle, where the two coincide.
 *
 * Accounts for `photoFill.rotation`: `photoFillImageTransform` applies rotation as the OUTERMOST
 * transform, spinning the already-panned photo about the frame's fixed center — so `offsetX`/
 * `offsetY` live in the photo's own unrotated local space, but what must stay covered is
 * `visualRect`, which (from that local space) effectively appears rotated by `-rotation` about
 * that same fixed center. The bound becomes "the covered rect contains `visualRect`'s rotated
 * corners," i.e. `visualRect`'s rotated axis-aligned bounding box. At `rotation` 0 this collapses
 * to exactly `visualRect` itself, so an unrotated photo's clamp is unchanged.
 */
export function computeCropPhotoPan(params: {
  frameWidth: number
  frameHeight: number
  /** The shape's tight visual box, local to the frame's own origin — see `visualRectLocalOfShape`.
   *  Defaults to the full frame (`{x: 0, y: 0, width: frameWidth, height: frameHeight}`), matching
   *  a plain rectangle where visual and geometric bounds are identical. */
  visualRect?: Rect
  photoFill: ShapePhotoFill
  localDx: number
  localDy: number
}): { offsetX: number; offsetY: number } {
  const { frameWidth, frameHeight, photoFill, localDx, localDy } = params
  const rawX = photoFill.offsetX + localDx
  const rawY = photoFill.offsetY + localDy
  const { naturalWidth, naturalHeight } = resolveEffectivePhotoFillNaturalSize(photoFill)
  const coveredSize = getCurrentPhotoFillCoveredSize(
    frameWidth,
    frameHeight,
    naturalWidth,
    naturalHeight,
    photoFill.scale
  )

  if (!coveredSize) return { offsetX: rawX, offsetY: rawY }

  const visualRect = params.visualRect ?? { x: 0, y: 0, width: frameWidth, height: frameHeight }
  const pivot: Point = { x: frameWidth / 2, y: frameHeight / 2 }
  const rotationDeg = photoFill.rotation ?? 0

  // `visualRect`'s corners as they appear in the photo's own un-rotated local space — see the doc
  // comment above for why rotating by `-rotationDeg` about the frame's fixed pivot is equivalent.
  const corners = rectCorners(visualRect).map((corner) =>
    rotatePointAbout(corner, pivot, -rotationDeg)
  )
  const minX = Math.min(...corners.map((p) => p.x))
  const maxX = Math.max(...corners.map((p) => p.x))
  const minY = Math.min(...corners.map((p) => p.y))
  const maxY = Math.max(...corners.map((p) => p.y))

  const halfCoveredW = coveredSize.coveredWidth / 2
  const halfCoveredH = coveredSize.coveredHeight / 2

  // The covered rect, centered at (pivot + offset), must contain [minX, maxX] x [minY, maxY]:
  //   pivot.x + offsetX - halfCoveredW <= minX  AND  pivot.x + offsetX + halfCoveredW >= maxX
  // — solved for offsetX (and the equivalent for Y).
  const minOffsetX = maxX - pivot.x - halfCoveredW
  const maxOffsetX = minX - pivot.x + halfCoveredW
  const minOffsetY = maxY - pivot.y - halfCoveredH
  const maxOffsetY = minY - pivot.y + halfCoveredH

  // Degenerate case (the covered rect is smaller than what needs covering on this axis, e.g. a
  // rotation that outgrows the current zoom): the solved range inverts (min > max). Clamping to
  // its midpoint is the safest fallback — for the symmetric (rectangle, unrotated) case that
  // midpoint is exactly 0, matching the previous hard-coded "no pan possible" behavior.
  const clampToRange = (raw: number, lo: number, hi: number): number =>
    lo > hi ? (lo + hi) / 2 : Math.min(hi, Math.max(lo, raw))

  return {
    offsetX: clampToRange(rawX, minOffsetX, maxOffsetX),
    offsetY: clampToRange(rawY, minOffsetY, maxOffsetY),
  }
}
